'use client';

import { useEffect } from 'react';
import Script from 'next/script';

// Add CSS import
const CSS_URL = "https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.css";

interface CookieConsentThis {
  options: {
    type: string;
  };
  hasConsented: () => boolean;
}

const cookieConsentConfig = {
  palette: {
    popup: {
      background: '#000',
      text: '#fff'
    },
    button: {
      background: '#4F46E5',
      text: '#fff'
    }
  },
  content: {
    message: 'We use cookies to enhance your experience. By continuing to visit this site you agree to our use of cookies.',
    dismiss: 'Accept',
    link: 'Learn more',
    href: '/cookie-policy'
  },
  type: 'opt-in' as const,
  onInitialise: function(this: CookieConsentThis) {
    const type = this.options.type;
    const didConsent = this.hasConsented();
    
    if (type === 'opt-in' && didConsent) {
      // Enable analytics and other scripts
      enableScripts();
    }
  },
  onStatusChange: function(this: CookieConsentThis) {
    const type = this.options.type;
    const didConsent = this.hasConsented();
    
    if (type === 'opt-in' && didConsent) {
      // Enable analytics and other scripts
      enableScripts();
    } else {
      // Disable analytics and other scripts
      disableScripts();
    }
  }
};

export function CookieConsent() {
  useEffect(() => {
    let linkElement: HTMLLinkElement | null = null;

    // Add CSS
    if (!document.querySelector(`link[href="${CSS_URL}"]`)) {
      linkElement = document.createElement('link');
      linkElement.rel = 'stylesheet';
      linkElement.href = CSS_URL;
      document.head.appendChild(linkElement);
    }

    // Initialize cookie consent after component mounts
    const initCookieConsent = () => {
      // Check if already initialized
      if (document.querySelector('.cc-window')) {
        return;
      }

      if (window.cookieconsent) {
        window.cookieconsent.initialise(cookieConsentConfig);
      } else {
        // If script hasn't loaded yet, wait for it
        setTimeout(initCookieConsent, 100);
      }
    };

    initCookieConsent();

    // Cleanup
    return () => {
      // Remove the CSS link we added
      if (linkElement && document.head.contains(linkElement)) {
        document.head.removeChild(linkElement);
      }
      // Also try to remove by query selector in case the element was added differently
      const existingLink = document.querySelector(`link[href="${CSS_URL}"]`);
      if (existingLink && existingLink !== linkElement) {
        existingLink.remove();
      }
    };
  }, []);

  return (
    <Script
      src="https://cdn.jsdelivr.net/npm/cookieconsent@3/build/cookieconsent.min.js"
      data-cfasync="false"
      strategy="afterInteractive"
    />
  );
}

// Add type declaration for cookieconsent
declare global {
  interface Window {
    cookieconsent?: {
      initialise: (options: typeof cookieConsentConfig) => void;
      hasConsented: () => boolean;
    };
    posthog?: {
      opt_in: () => void;
      opt_out: () => void;
    };
  }
}

// Helper functions to enable/disable scripts
function enableScripts() {
  // Enable PostHog
  if (window.posthog) {
    window.posthog.opt_in();
  }
  
  // Enable Google AdSense
  const adsenseScript = document.querySelector('script[src*="adsbygoogle.js"]');
  if (adsenseScript) {
    adsenseScript.setAttribute('data-cookieconsent', 'marketing');
  }
}

function disableScripts() {
  // Disable PostHog
  if (window.posthog) {
    window.posthog.opt_out();
  }
  
  // Disable Google AdSense
  const adsenseScript = document.querySelector('script[src*="adsbygoogle.js"]');
  if (adsenseScript) {
    adsenseScript.remove();
  }
} 